import {
  useCallback,
  useRef,
  useState,
  RefObject,
  KeyboardEvent,
  FocusEvent,
} from 'react';
import { SelectorListItem } from './index.d';

interface UseScrollKeyboardNavigationProps {
  containerRef: RefObject<HTMLDivElement | null>;
  items: SelectorListItem[];
  getItemDomId: (itemId: string | number) => string;
  multiple?: boolean;
  onToggle?: (item: SelectorListItem) => void;
}

interface UseScrollKeyboardNavigationReturn {
  handleKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
  handleFocus: (e: FocusEvent<HTMLDivElement>) => void;
  handleBlur: (e: FocusEvent<HTMLDivElement>) => void;
  handleMouseDown: () => void;
  activeItemId: string | number | null;
}

const useScrollKeyboardNavigation = ({
  containerRef,
  items,
  getItemDomId,
  multiple,
  onToggle,
}: UseScrollKeyboardNavigationProps): UseScrollKeyboardNavigationReturn => {
  const lastActiveItemIdRef = useRef<string | number | null>(null);
  const isMouseFocusRef = useRef(false);
  const [activeItemId, setActiveItemId] = useState<string | number | null>(
    null,
  );

  // Called on mousedown to mark that next focus is from mouse and clear active item
  const handleMouseDown = useCallback(() => {
    isMouseFocusRef.current = true;
    setActiveItemId(null);
  }, []);

  // Get first non-disabled item id
  const getFirstItemId = useCallback(() => {
    const firstEnabled = items.find(item => !item.disabled);
    return firstEnabled?.id ?? null;
  }, [items]);

  // Scroll active item into view using DOM id (only scrolls container, not window)
  const scrollItemIntoView = useCallback(
    (itemId: string | number) => {
      const container = containerRef.current;
      const domId = getItemDomId?.(itemId);
      const element = domId ? document.getElementById(domId) : null;

      if (!element || !container) return;

      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      // Check if element is above visible area
      if (elementRect.top < containerRect.top) {
        container.scrollTop -= containerRect.top - elementRect.top;
      }
      // Check if element is below visible area
      else if (elementRect.bottom > containerRect.bottom) {
        container.scrollTop += elementRect.bottom - containerRect.bottom;
      }
    },
    [containerRef, getItemDomId],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const onContainer = document.activeElement === container;

      // Arrow keys: navigate between items (virtual focus)
      if (onContainer && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        e.stopPropagation();

        const enabledItems = items.filter(item => !item.disabled);
        if (enabledItems.length === 0) return;

        const currentIndex = enabledItems.findIndex(
          item => item.id === activeItemId,
        );
        const direction = e.key === 'ArrowDown' ? 1 : -1;

        let nextIndex;
        if (currentIndex === -1) {
          // No active item, start from first or last
          nextIndex = direction === 1 ? 0 : enabledItems.length - 1;
        } else {
          nextIndex = currentIndex + direction;
        }

        // Stop at boundaries
        if (nextIndex < 0 || nextIndex >= enabledItems.length) {
          return;
        }

        const nextItem = enabledItems[nextIndex];
        setActiveItemId(nextItem.id);
        scrollItemIntoView(nextItem.id);

        // Single select: selection follows focus
        if (!multiple && onToggle) {
          onToggle(nextItem);
        }
        return;
      }

      // Enter/Space: toggle selection on active item
      if (onContainer && (e.key === 'Enter' || e.key === ' ')) {
        if (activeItemId !== null) {
          e.preventDefault();
          e.stopPropagation();
          const activeItem = items.find(item => item.id === activeItemId);
          if (activeItem && !activeItem.disabled && onToggle) {
            onToggle(activeItem);
          }
        }
        return;
      }
    },
    [containerRef, items, activeItemId, scrollItemIntoView, multiple, onToggle],
  );

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      // focus/blur bubble, so ignore focus landing on an inner element (a
      // toggle input receives focus when its label is clicked)
      if (e.target !== containerRef.current) {
        return;
      }

      // If focus came from mouse click, don't set active item
      if (isMouseFocusRef.current) {
        isMouseFocusRef.current = false;
        return;
      }

      // Restore saved item if returning from keyboard navigation
      if (lastActiveItemIdRef.current !== null) {
        const restoredId = lastActiveItemIdRef.current;
        setActiveItemId(restoredId);
        lastActiveItemIdRef.current = null;

        // Single select: also select the restored item
        if (!multiple && onToggle) {
          const restoredItem = items.find(item => item.id === restoredId);
          if (restoredItem && !restoredItem.disabled) {
            onToggle(restoredItem);
          }
        }
        return;
      }

      // Tab into container via keyboard - set first item as active
      const firstId = getFirstItemId();
      if (firstId !== null) {
        setActiveItemId(firstId);

        // Single select: also select the first item
        if (!multiple && onToggle) {
          const firstItem = items.find(item => item.id === firstId);
          if (firstItem && !firstItem.disabled) {
            onToggle(firstItem);
          }
        }
      }
    },
    [containerRef, getFirstItemId, items, multiple, onToggle],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      if (e.target !== containerRef.current) {
        return;
      }

      // Save and clear active item when container loses focus
      lastActiveItemIdRef.current = activeItemId;
      setActiveItemId(null);
    },
    [containerRef, activeItemId],
  );

  return {
    handleKeyDown,
    handleFocus,
    handleBlur,
    handleMouseDown,
    activeItemId,
  };
};

export default useScrollKeyboardNavigation;
